import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAssistantAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { LogoIcon } from '@/components/Logo';
import {
  Send,
  X,
  Loader2,
  Sparkles,
  ArrowRight,
  User,
  MessageSquareText
} from 'lucide-react';

const MAX_FREE_QUESTIONS = 1;
const STORAGE_KEY = 'dockhta_guest_questions';

const AIChatBubble = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load questions count from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      setQuestionsAsked(data.count || 0);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Add welcome message when first opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Bonjour ! 👋 Je suis l'assistant santé DOCKHTA. Posez-moi une question sur votre santé et je vous aiderai à mieux comprendre vos symptômes ou à vous orienter vers le bon spécialiste.\n\n⚠️ Je ne remplace pas une consultation médicale."
      }]);
    }
  }, [isOpen, messages.length]);

  const saveQuestionsCount = (count) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
      count, 
      lastAsked: new Date().toISOString() 
    }));
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    // Check if user has exceeded free questions
    if (questionsAsked >= MAX_FREE_QUESTIONS) {
      setShowSignupPrompt(true);
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Use guest endpoint for non-authenticated users
      const guestSessionId = `guest-${Date.now()}`;
      const response = await aiAssistantAPI.guestChat(input.trim(), guestSessionId);

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Update questions count
      const newCount = questionsAsked + 1;
      setQuestionsAsked(newCount);
      saveQuestionsCount(newCount);

      // Show signup prompt after response if limit reached
      if (newCount >= MAX_FREE_QUESTIONS) {
        setTimeout(() => setShowSignupPrompt(true), 1500);
      }

    } catch (error) {
      console.error('AI error:', error);
      // If rate limited or unauthorized, show signup
      if (error.response?.status === 429 || error.response?.status === 401) {
        setShowSignupPrompt(true);
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "Vous avez atteint la limite de questions gratuites. Créez un compte pour continuer à utiliser l'assistant IA sans limites !"
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "Désolé, une erreur s'est produite. Veuillez réessayer."
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSignup = () => {
    setIsOpen(false); // Fermer la fenêtre avant la navigation
    navigate('/register');
  };

  const handleLogin = () => {
    setIsOpen(false); // Fermer la fenêtre avant la navigation
    navigate('/login');
  };

  return (
    <>
      {/* Floating Button with CTA Label */}
      <div 
        className={`
          fixed bottom-6 right-6 z-50
          flex items-center gap-2
          transition-all duration-300
          ${isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}
        `}
      >
        {/* CTA Text Label */}
        <div 
          onClick={() => setIsOpen(true)}
          className="
            hidden sm:flex items-center gap-2
            bg-white px-4 py-2.5 rounded-full
            shadow-lg border border-slate-200
            cursor-pointer
            hover:shadow-xl hover:border-[#1976D2]/30
            transition-all duration-300
            group
          "
        >
          <MessageSquareText className="w-4 h-4 text-[#1976D2]" />
          <span className="text-sm font-medium text-slate-700 group-hover:text-[#1976D2] transition-colors">
            Discutez avec l'Assistant IA
          </span>
        </div>

        {/* Logo Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="
            w-16 h-16 rounded-full
            bg-white shadow-lg border-2 border-[#1976D2]/20
            flex items-center justify-center
            transition-all duration-300
            hover:scale-110 hover:shadow-xl hover:border-[#1976D2]/50
            overflow-hidden
            relative
          "
          data-testid="ai-bubble-trigger"
          aria-label="Ouvrir l'assistant IA DOCKHTA"
        >
          <LogoIcon size="md" />
          {/* Pulse notification indicator */}
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#E53935] rounded-full animate-pulse flex items-center justify-center">
            <span className="w-2 h-2 bg-white rounded-full" />
          </span>
        </button>
      </div>

      {/* Chat Window */}
      <div
        className={`
          fixed bottom-6 right-6 z-50
          w-[380px] max-w-[calc(100vw-2rem)]
          transition-all duration-300 origin-bottom-right
          ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}
        `}
      >
        <Card className="overflow-hidden shadow-2xl border-0 rounded-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1976D2] to-[#1A237E] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden p-1">
                <LogoIcon size="sm" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Assistant IA DOCKHTA</h3>
                <p className="text-xs text-white/80">Posez votre question santé</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="h-[350px] overflow-y-auto p-4 bg-slate-50">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`
                      max-w-[85%] rounded-2xl px-4 py-3 text-sm
                      ${message.role === 'user'
                        ? 'bg-[#1976D2] text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'}
                    `}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#1976D2]" />
                      <span className="text-sm text-slate-500">Réflexion en cours...</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Signup Prompt */}
              {showSignupPrompt && (
                <div className="bg-gradient-to-br from-[#E53935]/10 to-[#1976D2]/10 rounded-2xl p-3 sm:p-4 border border-[#E53935]/20">
                  <div className="flex flex-col sm:flex-row items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E53935] flex items-center justify-center flex-shrink-0 hidden sm:flex">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="w-full">
                      <p className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">
                        Continuez gratuitement !
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 mb-3">
                        Créez votre compte pour accéder à l&apos;assistant IA illimité, 
                        votre dossier médical et bien plus.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 w-full">
                        <Button
                          onClick={handleSignup}
                          className="w-full sm:w-auto bg-[#E53935] hover:bg-[#C62828] text-white text-sm rounded-full px-4 py-2"
                          data-testid="bubble-signup-btn"
                        >
                          Créer un compte
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                        <Button
                          onClick={handleLogin}
                          variant="outline"
                          className="w-full sm:w-auto text-sm rounded-full px-4 py-2"
                          data-testid="bubble-login-btn"
                        >
                          Se connecter
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t">
            {showSignupPrompt ? (
              <p className="text-xs text-center text-slate-500 py-2">
                Connectez-vous pour continuer la conversation
              </p>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-center gap-2"
              >
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Votre question santé..."
                  disabled={loading || questionsAsked >= MAX_FREE_QUESTIONS}
                  className="flex-1 h-10 rounded-full border-slate-200 text-sm"
                  data-testid="bubble-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="h-10 w-10 rounded-full bg-[#1976D2] hover:bg-[#1565C0] p-0"
                  data-testid="bubble-send-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </form>
            )}
            
            {!showSignupPrompt && questionsAsked === 0 && (
              <p className="text-xs text-center text-slate-400 mt-2">
                1 question gratuite • Créez un compte pour plus
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default AIChatBubble;
